// Jotai store singleton + Provider wrapper.
//
// Why this module exists: Jotai atoms read + write through a "store".
// By default each `<Provider>` has its own store, which means services
// outside React (background sync, pollers, the future mutation registry,
// future MCP tool handlers) can't read the same atoms the UI reads.
//
// Exporting one shared store and passing it to the root `<Provider>`
// fixes that — every `useAtom(myAtom)` in React reads from the same
// store that `jotaiStore.get(myAtom)` / `jotaiStore.set(myAtom, ...)`
// reads and writes from non-React code.
//
// Design per ADR 0022. Adoption is incremental — new cross-cutting
// state goes through Jotai; existing local-to-component React state
// stays as-is unless we're touching the area for other reasons.
//
// ADR: docs/adr/0022-jotai-atom-store.md

import { Provider, createStore } from 'jotai'

/**
 * The singleton store every atom read / write flows through. Created
 * once at module load; never re-created. Tests that need isolated state
 * should mount their own `<Provider>` without passing `store` (Jotai
 * falls back to a per-Provider implicit store).
 */
export const jotaiStore = createStore()

/**
 * Props-free Provider that wires the singleton store into React.
 * Mount once, above everything that uses atoms. Children see the
 * same store that non-React callers do via the `jotaiStore` export.
 */
export { Provider as JotaiProvider }
